/**
 * Regulatory Framework Determination
 * 
 * Correctly identifies which regulations apply based on vessel type and characteristics
 * Critical distinction: Workboats vs Passenger Vessels have completely different regulatory frameworks
 */

/**
 * Determine the primary regulatory framework for a vessel
 * @param {Object} vesselConfig - Vessel configuration
 * @returns {Object} Regulatory framework details
 */
export const determineRegulatoryFramework = (vesselConfig) => {
  const { type, length, passengerCapacity, category, operatingArea } = vesselConfig;
  
  // CRITICAL: Passenger vessels NEVER fall under WBC3
  if (type === 'pax' || type === 'PASSENGER') {
    return determinePassengerVesselRegulations(vesselConfig);
  }
  
  // Workboats can carry up to 12 passengers under WBC3
  if (passengerCapacity > 12) {
    return {
      primaryCode: 'INVALID_CONFIGURATION',
      error: 'Workboats cannot carry more than 12 passengers under WBC3',
      recommendation: 'Vessel must be classified as passenger vessel and comply with passenger ship regulations'
    };
  }
  
  // All other vessel types fall under WBC3
  return {
    primaryCode: 'WBC3',
    fullName: 'UK Workboat Code Edition 3',
    regulations: [
      'UK Workboat Code Edition 3',
      'SI 1999/2722 (Fire & Abandon Ship Drills)',
      'MGN 280 (Small Vessel Training)',
      'MIN 698 (WBC3 Application Guidance)'
    ],
    drillsRegulation: 'SI 1999/2722',
    medicalRegulation: 'MSN 1886',
    certificatesRegulation: 'MIN 524',
    maxPassengers: 12,
    note: 'Workboat carrying up to 12 passengers'
  };
};

/**
 * Determine regulations for passenger vessels
 * @param {Object} vesselConfig - Vessel configuration
 * @returns {Object} Passenger vessel regulatory framework
 */
const determinePassengerVesselRegulations = (vesselConfig) => {
  const { length, passengerCapacity, operatingArea, category } = vesselConfig;
  
  // UK domestic passenger vessel codes based on length
  if (length < 24) {
    return {
      primaryCode: 'UK_DOMESTIC_PASSENGER',
      fullName: 'UK Domestic Passenger Ship Regulations',
      regulations: [
        'MSN 1823 - Safety Code for Passenger Ships Operating Solely in UK Categorised Waters',
        'SI 1999/2722 (Fire & Abandon Ship Drills)',
        'MGN 535 (Passenger Ship Emergency Information)',
        'MIN 592 (Passenger Numbers and Stability)'
      ],
      classSystem: determinePassengerClass(length, passengerCapacity, category),
      drillsRegulation: 'SI 1999/2722',
      medicalRegulation: 'MSN 1886',
      certificatesRegulation: 'MSN 1856',
      SOLAS_applicable: false,
      SOLAS_note: 'Exempted - operates under UK domestic passenger codes',
      maxPassengers: 'As per stability book and passenger certificate',
      note: 'Small passenger vessel under UK domestic codes'
    };
  }
  
  // Larger vessels may fall under SOLAS
  return {
    primaryCode: 'SOLAS',
    fullName: 'International Convention for Safety of Life at Sea',
    regulations: [
      'SOLAS Chapter I-XII',
      'SI 1999/2722 (Fire & Abandon Ship Drills)',
      'ISM Code (if applicable)',
      'ISPS Code (if applicable)'
    ],
    drillsRegulation: 'SOLAS Chapter III + SI 1999/2722',
    medicalRegulation: 'MLC 2006',
    certificatesRegulation: 'STCW Convention',
    SOLAS_applicable: true,
    maxPassengers: 'As per Passenger Ship Safety Certificate',
    note: 'International passenger vessel'
  };
};

/**
 * Determine UK passenger vessel class
 * @param {number} length - Vessel length in meters
 * @param {number} passengers - Number of passengers
 * @param {string} category - Operating category
 * @returns {string} UK passenger class
 */
const determinePassengerClass = (length, passengers, category) => {
  // Simplified logic - actual classification is complex
  if (passengers <= 12) {
    return 'Class VI(A) - Up to 12 passengers';
  } else if (passengers <= 50) {
    return 'Class V - Up to 50 passengers';
  } else if (passengers <= 250) {
    return 'Class IV - Up to 250 passengers';
  } else {
    return 'Class II/III - Over 250 passengers';
  }
};

/**
 * Determine SOLAS applicability
 * @param {Object} vesselConfig - Vessel configuration
 * @returns {Object} SOLAS determination
 */
export const determineSolasApplicability = (vesselConfig) => {
  const { type, length, passengerCapacity, operatingArea } = vesselConfig;
  
  // Passenger vessels
  if (type === 'pax' || type === 'PASSENGER') {
    if (length >= 24 && operatingArea === 'international') {
      return {
        applicable: true,
        reason: 'Passenger ship ≥24m on international voyages',
        note: 'Full SOLAS compliance required'
      };
    }
    return {
      applicable: false,
      reason: 'Small passenger vessel <24m - UK domestic codes apply',
      note: 'Operates under MSN 1823 or equivalent'
    };
  }
  
  // Workboats - SOLAS rarely applies
  if (length >= 500) { // 500 GT approximately
    return {
      applicable: true,
      reason: 'Large vessel subject to SOLAS',
      note: 'Workboat over 500GT'
    };
  }
  
  return {
    applicable: false,
    reason: 'Workboat under SOLAS thresholds',
    note: 'Operates under WBC3'
  };
};

/**
 * Get drill requirements based on regulatory framework
 * @param {Object} framework - Regulatory framework from determineRegulatoryFramework
 * @param {string} drillType - Type of drill
 * @returns {Object} Drill requirement details
 */
export const getDrillRequirementByFramework = (framework, drillType) => {
  const baseRequirements = {
    fire: {
      frequency_days: 30,
      compliance_level: 'Required',
      regulatory_source: 'SI 1999/2722 Regulation 8(2)',
      note: 'Monthly drills required by law - applies to ALL vessels'
    },
    abandon_ship: {
      frequency_days: 30,
      compliance_level: 'Required',
      regulatory_source: 'SI 1999/2722 Regulation 8(2)',
      note: 'Monthly drills required by law - applies to ALL vessels'
    },
    mob: {
      frequency_days: 90,
      compliance_level: 'Required',
      regulatory_source: framework.primaryCode === 'WBC3' ? 'WBC3 Section 14.11.2' : 'Company SMS',
      note: 'Frequency per SMS interpretation of "regularly"'
    }
  };
  
  // Additional drills are SMS-based, not regulatory
  const smsBasedDrills = {
    medical_emergency: {
      frequency_days: 180,
      compliance_level: 'Recommended',
      regulatory_source: 'Company SMS / Risk Assessment',
      note: 'Frequency determined by vessel risk assessment'
    },
    steering_nav_fail: {
      frequency_days: 180,
      compliance_level: 'Recommended',
      regulatory_source: 'Company SMS / Risk Assessment',
      note: 'Recommended for vessels with emergency steering'
    },
    pollution_response: {
      frequency_days: 180,
      compliance_level: 'Recommended',
      regulatory_source: 'MARPOL / Company SMS',
      note: 'Required if SOPEP applicable'
    }
  };
  
  return baseRequirements[drillType] || smsBasedDrills[drillType] || {
    frequency_days: 180,
    compliance_level: 'Optional',
    regulatory_source: 'Company SMS',
    note: 'Frequency per company procedures'
  };
};

/**
 * Validate vessel configuration
 * @param {Object} vesselConfig - Vessel configuration
 * @returns {Object} Validation result
 */
export const validateVesselConfiguration = (vesselConfig) => {
  const { type, passengerCapacity } = vesselConfig;
  const errors = [];
  const warnings = [];
  
  // Check passenger capacity vs vessel type
  if (type !== 'pax' && type !== 'PASSENGER' && passengerCapacity > 12) {
    errors.push({
      field: 'passengerCapacity',
      message: 'Workboats cannot carry more than 12 passengers under WBC3',
      action: 'Either reduce passenger capacity or reclassify as passenger vessel'
    });
  }
  
  // Check if passenger vessel is trying to use WBC3 categories
  if ((type === 'pax' || type === 'PASSENGER') && vesselConfig.regulatoryCode === 'WBC3') {
    errors.push({
      field: 'regulatoryCode',
      message: 'Passenger vessels cannot operate under WBC3',
      action: 'Passenger vessels must comply with MSN 1823 or equivalent'
    });
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

export default {
  determineRegulatoryFramework,
  determineSolasApplicability,
  getDrillRequirementByFramework,
  validateVesselConfiguration
};